/*
 * Above 9 · Tranquitone · looping section video (original work, theme v0.1, 2026-10-04).
 *
 * A muted loop with a pause button (WCAG 2.2.2: moving content that runs longer than five seconds can be paused).
 * The video loads nothing until it plays (preload none, the poster first) and plays only while it is on screen and
 * shown (phones keep the still picture, so a hidden desktop video never plays or downloads). Visitors who ask for
 * reduced motion get the poster and can press play themselves. A press on pause is respected until they press play.
 * The button's name follows the video's real state, using Horizon's own "Pause video" and "Play video" strings.
 */

class A9LoopVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = /** @type {HTMLButtonElement | null} */ (this.querySelector('.a9-loop__toggle'));
    if (!this.video || !this.button) return;

    this.video.muted = true;
    this.video.setAttribute('playsinline', '');
    this.video.removeAttribute('autoplay');
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.userPaused = this.reduce.matches;

    this.sync = () => {
      const paused = this.video ? this.video.paused : true;
      if (!this.button) return;
      this.button.setAttribute('aria-pressed', paused ? 'true' : 'false');
      this.button.setAttribute(
        'aria-label',
        (paused ? this.button.dataset.labelPlay : this.button.dataset.labelPause) || ''
      );
      this.classList.toggle('is-paused', paused);
    };

    this.onClick = () => {
      if (!this.video) return;
      if (this.video.paused) {
        this.userPaused = false;
        this.video.play().catch(() => this.sync && this.sync());
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    };

    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!this.video) return;
        if (entry.isIntersecting && !this.userPaused) {
          this.video.play().catch(() => this.sync && this.sync());
        } else if (!entry.isIntersecting && !this.video.paused) {
          this.video.pause();
        }
      }
    });

    this.video.addEventListener('play', this.sync);
    this.video.addEventListener('pause', this.sync);
    this.button.addEventListener('click', this.onClick);
    this.observer.observe(this);
    this.sync();
  }

  disconnectedCallback() {
    if (this.video && this.sync) {
      this.video.removeEventListener('play', this.sync);
      this.video.removeEventListener('pause', this.sync);
    }
    if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
    if (this.observer) this.observer.disconnect();
  }
}

if (!customElements.get('a9-loop-video')) customElements.define('a9-loop-video', A9LoopVideo);
